// Notification bell for the NavBar. Renders nothing when the user has no
// notifications at all; otherwise a bell with an unread-count badge that opens a
// dropdown list. Clicking an item marks it read and dispatches its action
// (notificationTarget). Refetches whenever `refreshSignal` changes — NavBar
// bumps it from the MQTT notifications-updated push so the bell stays live.
import React, {useCallback, useEffect, useState} from "react";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {
    IconButton, Badge, Popover, Box, Typography, Button, Divider, Tooltip, CircularProgress,
} from "@mui/material";
import {NotificationsNoneOutlined} from "@mui/icons-material";
import {useNavigate} from "react-router";
import {useSessionContext} from "../../util/SessionContext";
import {
    AppNotification, fetchNotifications, markAllNotificationsRead, markNotificationRead, notificationTarget,
} from "../../data/notifications";

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const TEXT = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E4E6E8";
const BLUE = "#1498D8";

function appsLabel(applications: string[] | null): string {
    if (!applications || applications.length === 0) return "All apps";
    return applications.join(", ");
}

function relativeTime(iso: string): string {
    if (!iso) return "";
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return "";
    const mins = Math.floor((Date.now() - t) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(t).toLocaleDateString(undefined, {month: "short", day: "numeric"});
}

interface Props {
    /** Bumped by NavBar on each MQTT notifications-updated push -> refetch. */
    refreshSignal: number;
}

export default function NotificationBell({refreshSignal}: Props) {
    const navigate = useNavigate();
    // BUILT FROM THE SESSION, like every other t1 screen: the token lives on the session context and
    // there is no module-level instance to import here as there is in t5.
    const sessionContext = useSessionContext();
    const api = React.useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [items, setItems] = useState<AppNotification[]>([]);
    const [unread, setUnread] = useState(0);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchNotifications(api);
            setItems(res.notifications);
            setUnread(res.unreadCount);
        } catch {
            /* keep prior state on failure */
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        load();
    }, [load, refreshSignal]);

    const handleItemClick = async (n: AppNotification) => {
        setAnchorEl(null);
        if (!n.read) {
            setItems((prev) => prev.map((x) => (x.usid === n.usid ? {...x, read: true} : x)));
            setUnread((u) => Math.max(0, u - 1));
            try {
                await markNotificationRead(api, n.usid);
            } catch {
                /* best-effort */
            }
        }
        const target = notificationTarget(n);
        if (target) navigate(target);
    };

    const handleMarkAll = async () => {
        setItems((prev) => prev.map((x) => ({...x, read: true})));
        setUnread(0);
        try {
            await markAllNotificationsRead(api);
        } catch {
            /* best-effort */
        }
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" aria-label="notifications">
                    <Badge
                        badgeContent={unread}
                        color="error"
                        overlap="circular"
                        sx={{"& .MuiBadge-badge": {fontSize: 10, height: 16, minWidth: 16}}}
                    >
                        <NotificationsNoneOutlined />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{paper: {sx: {width: 380, maxWidth: "90vw", fontFamily: FONT}}}}
            >
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25}}>
                    <Typography sx={{fontSize: 15, fontWeight: 700, color: TEXT, fontFamily: FONT}}>Notifications</Typography>
                    {unread > 0 && (
                        <Button
                            onClick={handleMarkAll}
                            size="small"
                            sx={{textTransform: "none", fontFamily: FONT, fontSize: 12, color: BLUE, minWidth: 0}}
                        >
                            Mark all read
                        </Button>
                    )}
                </Box>
                <Divider />
                <Box sx={{maxHeight: 420, overflowY: "auto"}}>
                    {loading && items.length === 0 ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 3}}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : items.length === 0 ? (
                        <Box sx={{px: 2, py: 4, textAlign: "center"}}>
                            <Typography sx={{fontSize: 13, color: MUTED, fontFamily: FONT}}>
                                No notifications yet
                            </Typography>
                        </Box>
                    ) : (
                        items.map((n) => (
                            <Box
                                key={n.usid}
                                onClick={() => handleItemClick(n)}
                                sx={{
                                    display: "flex", gap: 1, alignItems: "flex-start", px: 2, py: 1.25, cursor: "pointer",
                                    backgroundColor: n.read ? "#FFF" : "#F3F9FD",
                                    borderBottom: `1px solid ${BORDER}`,
                                    "&:hover": {backgroundColor: n.read ? "#F7F7F7" : "#E9F4FB"},
                                }}
                            >
                                <Box sx={{width: 8, mt: "6px", flexShrink: 0}}>
                                    {!n.read && <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: BLUE}} />}
                                </Box>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Typography sx={{fontSize: 13, fontWeight: 600, color: TEXT, fontFamily: FONT}}>{n.title}</Typography>
                                    {n.body && (
                                        <Typography
                                            sx={{
                                                fontSize: 12.5, color: TEXT, fontFamily: FONT,
                                                overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                                                WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                            }}
                                        >
                                            {n.body}
                                        </Typography>
                                    )}
                                    <Box sx={{display: "flex", gap: 0.75, alignItems: "center", mt: 0.5}}>
                                        <Typography sx={{fontSize: 11, color: MUTED, fontFamily: FONT}}>{appsLabel(n.applications)}</Typography>
                                        <Typography sx={{fontSize: 11, color: MUTED, fontFamily: FONT}}>
                                            · {relativeTime(n.createdDatetime)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            </Popover>
        </>
    );
}
